import { useEffect, useCallback } from "react";
import { tracker } from "@openreplay/tracker";
import trackerAssist from "@openreplay/tracker-assist";
import { useStore } from "@/mstore";
import { sessionService } from "@/services";
import { observer } from "mobx-react-lite";
import "./init/openreplay";
import ENV from "../env";

const USER_ID_TRACK_INTERVAL = 2 * 60 * 1000; // 2 minutes

/**
 * Tracker component that initializes and manages OpenReplay session tracking
 * Handles user identification, metadata tracking, and assist functionality
 */
function Tracker() {
  console.debug("Tracker component installed");
  const { userStore } = useStore();
  const { account, isLoggedIn } = userStore;
  const { email, plan, name } = account || {};

  const setUserIDIfEmail = useCallback(() => {
    if (!email) return;
    tracker.setUserID(email);
    tracker.getInstance()?.analytics?.people.set("name", name);
  }, [email]);

  const setPlanMetadata = useCallback(() => {
    if (!plan) return;
    tracker.setMetadata("plan", plan?.type);
    tracker.analytics?.people?.set("plan", plan.type);
  }, [plan]);

  /**
   * Initialize assist credentials for OpenReplay
   * @returns Promise that resolves when credentials are successfully fetched and applied
   */
  const initializeAssistCredentials = useCallback(async () => {
    try {
      const { data } = await sessionService.getAssistCredentials();
      tracker.use(
        trackerAssist({
          config: {
            iceServers: data,
            sdpSemantics: "unified-plan",
          },
        }),
      );
      return true;
    } catch (e) {
      console.error("Error fetching assist credentials:", e);
      return false;
    }
  }, []);

  /**
   * Start a new OpenReplay tracking session
   * @returns Promise that resolves when session is successfully started
   */
  const startOpenReplaySession = useCallback(async () => {
    try {
      const metadata: Record<string, string> = {}; // More specific type
      if (plan?.type) metadata["plan"] = plan.type;
      console.debug("Starting with metadata:", metadata);

      const res = await tracker.start({ userID: email, metadata });
      console.debug("OpenReplay session started:", res);
      return true;
    } catch (error) {
      console.error("Error starting OpenReplay session:", error);
      return false;
    }
  }, [email, plan?.type]);

  useEffect(() => {
    setPlanMetadata();
  }, [plan]);

  useEffect(() => {
    setUserIDIfEmail();
  }, [email]);

  useEffect(() => {
    if (!email || !isLoggedIn) return;

    let intervalId: number | null = null; // Using browser's window.setInterval type

    /**
     * Check if tracker should be enabled based on environment
     */
    const isTrackerEnabled = () => {
      const urlQuery = new URLSearchParams(window.location.search);
      const shouldSkip = urlQuery.get("skip_tracker") === "true";
      if (shouldSkip) {
        console.log("Tracker disabled via URL parameter.");
        return false;
      }
      const isLocalhost = window.location.hostname === "localhost";
      return (
        !isLocalhost &&
        ENV.TRACKER_ENABLED === "true" &&
        ENV.TRACKER_PROJECT_KEY !== ""
      );
    };

    /**
     * Set up periodic user ID tracking
     */
    const setupPeriodicTracking = () => {
      if (intervalId) window.clearInterval(intervalId);
      intervalId = window.setInterval(() => {
        setUserIDIfEmail();
      }, USER_ID_TRACK_INTERVAL);
    };

    /**
     * Initialize the OpenReplay tracker
     */
    const initializeTracker = async () => {
      if (!isTrackerEnabled()) {
        return console.log("tracker skipped");
      }

      const assistInitialized = await initializeAssistCredentials();
      const sessionStarted = await startOpenReplaySession();

      if (assistInitialized && sessionStarted) {
        setupPeriodicTracking();
      }
    };

    initializeTracker().catch((error) => {
      console.error("Tracker initialization failed:", error);
    });

    return () => {
      if (intervalId) window.clearInterval(intervalId);
      if (tracker.isActive()) {
        tracker.stop();
      }
    };
  }, [
    isLoggedIn,
    email,
    initializeAssistCredentials,
    startOpenReplaySession,
    setUserIDIfEmail,
  ]);

  return null;
}

export default observer(Tracker);
